// src/views/proprio/ProprioMenageView.jsx — Permissions 42 Phase 2 (espace proprio)
//
// Onglet UNIQUE « Ménage & incidents » (tout incident est lié à un passage).
// LECTURE SEULE. Structure :
//   1. Mode « Tout » (historique complet) OU navigation par mois (‹ Mois AAAA ›
//      + ↩ Aujourd'hui), EXCLUSIFS, + logement — AGISSENT SUR TOUT (stats + liste).
//   2. Barre de 3 stats : « X passages » (compteur) · « X incident(s) »
//      (cliquable → détail) · « X € remboursé » (cliquable → détail).
//   3. Liste des passages par logement (cliquables) + badge incident.
//   4. Au clic d'un passage : checklist détaillée (✓/○) par pièce + photos SOUS
//      CHAQUE PIÈCE (visionneuse plein écran) + incident complet inline.
//
// Cloisonné logIds côté serveur. Champs internes FBZ JAMAIS renvoyés. Photos =
// signed URL bucket privé (vides tant que l'upload mobile n'est pas repointé).
// Briques incident mutualisées (ProprioIncidentBits). Escaping React natif.

import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { compterPoints, compterConfigurees, phraseChecklist } from '../../lib/checklist-lecture'
import {
  Sparkles, Home, Users, AlertTriangle, Check, Circle, Camera,
  ChevronLeft, ChevronRight, ChevronDown, RotateCcw, CheckCircle2, Lock, Clock, CalendarDays,
  Shield, ShoppingBasket, Receipt,
} from 'lucide-react'
import useEtroit from '../../lib/useEtroit'
import { DS } from '../../lib/ui'
import { fmtDate, fmtE } from '../../lib/format'
import { isMenageRunLockedForMenage, getEffectiveRunStatus } from '../../lib/menage-status-helpers'
import { fetchProprioMenage, fetchProprioCalendar, fetchProprioIncidents, fetchProprioLogements } from '../../lib/proprio-api'
import { card, SectionTitle, Loading, ErrorBox, EmptyBox, ReadOnlyHint } from './_proprioUi'
import { IncidentFull } from './ProprioIncidentBits'
import ProprioPhotoViewer from './ProprioPhotoViewer'

// Nombre de passages affichés par logement avant le repli « voir les N autres ».
const RUNS_PREVIEW = 3

function wideRange() {
  const y = new Date().getFullYear()
  return { from: `${y - 2}-01-01`, to: `${y + 1}-12-31` }
}

const ZONE_LABELS = {
  salon: 'Salon', cuisine: 'Cuisine', sdb: 'Salle de bain', chambre: 'Chambre',
  entree: 'Entrée', couloir: 'Couloir', wc: 'WC', buanderie: 'Buanderie',
  exterieur: 'Extérieur', terrasse: 'Terrasse',
}

function toISO(d) {
  const p = (n) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
}

// Statut date-aware (ADR-207), côté PROPRIO (lecture seule) :
//  - au-delà de 48h (scheduled_date + 2j) → « 🔒 Fermé » : période close. PAS de
//    distinction fait/non saisi côté proprio (pour ne pas l'inquiéter) — l'admin,
//    lui, voit « Fermé · fait » / « Fermé · non saisi ».
//  - passé ≤48h non clôturé → « Non renseigné » en BLEU LOGO (encore remplissable
//    par l'équipe/admin) ; rien n'est rétro-écrit en base (levier affichage seul).
function statusInfo(run) {
  const today = toISO(new Date())
  if (isMenageRunLockedForMenage(run.scheduled_date, today)) return { label: 'Fermé', color: DS.textMuted, icon: Lock }
  if (run.completed_at || run.status === 'completed') return { label: 'Terminé', color: DS.successText, icon: CheckCircle2 }
  if (run.status === 'in_progress') return { label: 'En cours', color: DS.warningText, icon: Clock }
  const d = String(run.scheduled_date || '').slice(0, 10)
  if (d && d < today) return { label: 'Non renseigné', color: DS.marine, icon: AlertTriangle }
  return { label: 'Prévu', color: DS.textMuted, icon: CalendarDays }
}

// Libellé de date d'en-tête, cohérent avec le statut (jamais « Prévu le » dans
// le passé : un passage passé non clôturé devient « Passage du »).
function headerDateLabel(run) {
  if (run.completed_at) return `Terminé le ${fmtDate(run.completed_at)}`
  const d = String(run.scheduled_date || '').slice(0, 10)
  if (d && d < toISO(new Date())) return `Passage du ${fmtDate(run.scheduled_date)}`
  return `Prévu le ${fmtDate(run.scheduled_date)}`
}

// Navigation par mois (pérenne, année toujours affichée).
const MONTHS_FR = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre']
function currentMonthKey() { return toISO(new Date()).slice(0, 7) }
function monthLabel(mk) {
  const [y, m] = String(mk).split('-').map(Number)
  const name = MONTHS_FR[(m || 1) - 1] || ''
  return `${name.charAt(0).toUpperCase()}${name.slice(1)} ${y}`
}
function shiftMonth(mk, delta) {
  const [y, m] = String(mk).split('-').map(Number)
  return toISO(new Date(y, m - 1 + delta, 1)).slice(0, 7)
}

function runDateISO(run) {
  return String(run.completed_at || run.scheduled_date || '').slice(0, 10)
}

// Date d'un incident pour le filtrage mensuel (incident = ponctuel).
function incidentDateISO(inc) {
  return String(inc.incident_date || inc.reported_at || '').slice(0, 10)
}

// Lecture UNIQUE d'items_checked — module partagé (checklist-lecture.js).
function checklistSummary(itemsChecked) {
  const { coches } = compterPoints(itemsChecked)
  return { total: coches }
}

function humanize(key) {
  const s = String(key || '').replace(/^[a-z]+_/, '').replace(/_/g, ' ').trim()
  if (!s) return String(key || '')
  return s.charAt(0).toUpperCase() + s.slice(1)
}

// Détail point par point d'une checklist + photos SOUS CHAQUE PIÈCE. Tous les
// points ✓ (fait) / ○ (non coché). Photos cliquables → visionneuse (onPhotoClick
// reçoit l'index global du passage).
function ChecklistDetail({ itemsChecked, template, photosByPiece, onPhotoClick }) {
  const checked = itemsChecked && typeof itemsChecked === 'object' ? itemsChecked : {}
  const byPiece = photosByPiece || {}

  const Line = ({ done, label }) => (
    <div style={{ fontSize: 12, color: done ? DS.textPrimary : DS.textMuted, display: 'flex', gap: 6, alignItems: 'center', padding: '2px 0' }}>
      <span style={{ display: 'inline-flex', flex: '0 0 auto', color: done ? DS.success : DS.borderHover }}>
        {done ? <Check size={13} strokeWidth={2.2} /> : <Circle size={13} strokeWidth={1.8} />}
      </span>
      <span>{label}</span>
    </div>
  )

  // Vignettes photos (colonne DROITE) : format CARTE confortable, ~2 par ligne,
  // alignées à droite. Le clic ouvre la visionneuse plein écran.
  const Thumbs = ({ list }) => (Array.isArray(list) && list.length) ? (
    <div style={{ flexShrink: 0, display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'flex-end', maxWidth: 200, width: 'auto' }}>
      {list.map((p) => (
        <img key={p.idx} src={p.signedUrl} alt={p.fileName || ''} onClick={() => onPhotoClick && onPhotoClick(p.idx)}
          style={{ width: 92, height: 92, objectFit: 'cover', borderRadius: 8, border: `0.5px solid ${DS.border}`, cursor: 'pointer', boxShadow: '0 1px 3px rgba(33,83,135,.08)' }} />
      ))}
    </div>
  ) : null

  // Bloc d'une pièce : titre, puis 2 colonnes — checklist à GAUCHE, photos à DROITE.
  // `icon` = libellé du template (donnée) ; `IconCmp` = icône lucide optionnelle.
  const Piece = ({ icon, IconCmp, name, lines, pics }) => (
    <div style={{ marginTop: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12.5, fontWeight: 700, color: DS.marineDark, marginBottom: 3 }}>
        {IconCmp && <IconCmp size={14} strokeWidth={1.8} color={DS.marine} />}
        <span>{icon ? `${icon} ` : ''}{name}</span>
      </div>
      <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 170 }}>{lines}</div>
        <Thumbs list={pics} />
      </div>
    </div>
  )

  const hasTemplate = Array.isArray(template) && template.length
  // Zones à rendre (template prioritaire ; sinon zones cochées).
  const zonesKeys = hasTemplate ? template.map((p) => p.id) : Object.keys(checked).filter((z) => Array.isArray(checked[z]) && checked[z].length)
  const knownIds = new Set(zonesKeys)

  return (
    <div>
      {hasTemplate ? template.map((piece) => {
        const items = Array.isArray(piece.items) ? piece.items : []
        const set = new Set(Array.isArray(checked[piece.id]) ? checked[piece.id] : [])
        const tplItemIds = new Set(items.map((it) => it.id))
        const extras = [...set].filter((id) => !tplItemIds.has(id))
        const pics = byPiece[piece.id]
        if (!items.length && !(pics && pics.length)) return null
        return (
          <Piece key={piece.id} icon={piece.icon} name={piece.name} pics={pics}
            lines={<>
              {items.map((it) => <Line key={it.id} done={set.has(it.id)} label={it.label} />)}
              {extras.map((id) => <Line key={id} done label={humanize(id)} />)}
            </>} />
        )
      }) : zonesKeys.map((zone) => (
        <Piece key={zone} name={ZONE_LABELS[zone] || humanize(zone)} pics={byPiece[zone]}
          lines={(checked[zone] || []).map((id) => <Line key={id} done label={humanize(id)} />)} />
      ))}

      {/* Zones cochées hors template (template modifié depuis le passage). */}
      {hasTemplate && Object.entries(checked).filter(([z, arr]) => !knownIds.has(z) && Array.isArray(arr) && arr.length).map(([zone, ids]) => (
        <Piece key={zone} name={ZONE_LABELS[zone] || humanize(zone)} pics={byPiece[zone]}
          lines={ids.map((id) => <Line key={id} done label={humanize(id)} />)} />
      ))}

      {/* Rien à montrer : on DIT lequel des deux vides c'est. */}
      {!hasTemplate && !zonesKeys.length && (byPiece.__none__ || []).length === 0 && (
        <div style={{ fontSize: 11.5, color: DS.textMuted }}>
          {phraseChecklist(itemsChecked, compterConfigurees(template))}
        </div>
      )}

      {/* Photos sans pièce identifiée. */}
      {(byPiece.__none__ || []).length > 0 && (
        <Piece IconCmp={Camera} name="Autres photos" pics={byPiece.__none__} lines={null} />
      )}
    </div>
  )
}

function MenageRow({ run, guest, incidents, template, expanded, onToggle, onReload, refReg }) {
  const etroit = useEtroit()
  const bandeH = etroit ? 56 : 66
  const st = statusInfo(run)
  const { total } = useMemo(() => checklistSummary(run.items_checked), [run.items_checked])
  const nbInc = incidents.length
  const [viewerIdx, setViewerIdx] = useState(null)

  // Photos ordonnées par pièce (ordre template) + index global pour la visionneuse.
  const orderedPhotos = useMemo(() => {
    const raw = Array.isArray(run.photos) ? run.photos : []
    const tpl = Array.isArray(template) ? template : []
    const order = tpl.map((p) => p.id)
    const nameOf = (pid) => {
      const p = tpl.find((x) => x.id === pid)
      return p ? `${p.icon || ''} ${p.name}`.trim() : (pid ? (ZONE_LABELS[pid] || humanize(pid)) : 'Autres')
    }
    const rank = (pid) => { const i = order.indexOf(pid); return i === -1 ? 999 : i }
    return raw
      .map((p) => ({ ...p, pieceName: nameOf(p.piece_id) }))
      .sort((a, b) => rank(a.piece_id) - rank(b.piece_id))
      .map((p, idx) => ({ ...p, idx }))
  }, [run.photos, template])

  const photosByPiece = useMemo(() => {
    const m = {}
    for (const p of orderedPhotos) (m[p.piece_id || '__none__'] ||= []).push(p)
    return m
  }, [orderedPhotos])

  const nbPhotos = orderedPhotos.length

  return (
    <div ref={refReg} style={{ borderTop: `1px solid ${DS.border}`, overflow: 'hidden' }}>
      <button onClick={onToggle} className="fbz-rowhover"
        style={{ display: 'block', width: '100%', textAlign: 'left', border: 'none', background: 'none', cursor: 'pointer', padding: '12px 14px', fontFamily: DS.fontBody }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', fontWeight: 700, color: DS.marine, fontSize: 13 }}>
            <span style={{ display: 'inline-flex', color: DS.textMuted, marginRight: 6 }}>{expanded ? <ChevronDown size={15} strokeWidth={1.8} /> : <ChevronRight size={15} strokeWidth={1.8} />}</span>
            {headerDateLabel(run)}
            {guest && <span style={{ color: DS.textSecondary, fontWeight: 600 }}>&nbsp;· {guest}</span>}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            {nbInc > 0 && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: DS.dangerBg, color: DS.dangerText, borderRadius: 7, padding: '2px 8px', fontSize: 10.5, fontWeight: 600 }}><AlertTriangle size={11} strokeWidth={1.8} /> {nbInc} incident{nbInc > 1 ? 's' : ''}</span>}
            {run.guest_count != null && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 11, color: DS.textMuted }}><Users size={12} strokeWidth={1.8} /> {run.guest_count}</span>}
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: st.color + '22', color: st.color, borderRadius: 7, padding: '2px 9px', fontSize: 10.5, fontWeight: 600 }}><st.icon size={11} strokeWidth={1.8} /> {st.label}</span>
          </div>
        </div>
        {!expanded && (
          <div style={{ fontSize: 11, color: DS.textMuted, marginTop: 4 }}>
            {/* ⚠️ LES DEUX VIDES NE SE CONFONDENT PLUS (Dimitri 2026-08-22) —
                phrase IDENTIQUE à celle de l'orchestrateur (module partagé) :
                aucune ligne configurée = trou de paramétrage FBZ ; configurée
                et rien coché = manquement, et il se chiffre. */}
            {phraseChecklist(run.items_checked, compterConfigurees(template))}
            {nbPhotos > 0 ? ` · ${nbPhotos} photo${nbPhotos > 1 ? 's' : ''}` : ''}
          </div>
        )}
      </button>

      {/* LES PHOTOS DIRECTEMENT, sans ouvrir (spec §6) : bande de vignettes
          66 px (56 au téléphone) + vignette « voir » en pointillés → détail. */}
      {!expanded && nbPhotos > 0 && (
        <div style={{ display: 'flex', gap: 6, overflowX: 'auto', padding: '0 14px 12px' }}>
          {orderedPhotos.slice(0, 8).map((ph) => (
            <img key={ph.idx} src={ph.signedUrl || ph.url} alt={ph.pieceName || ''}
              onClick={() => setViewerIdx(ph.idx)}
              style={{ height: bandeH, width: Math.round(bandeH * 1.25), objectFit: 'cover',
                borderRadius: 8, border: `0.5px solid ${DS.border}`, cursor: 'pointer', flexShrink: 0 }} />
          ))}
          <button type="button" onClick={onToggle} aria-label="Voir le détail du passage"
            className="fbz-press"
            style={{ height: bandeH, width: Math.round(bandeH * 1.05), flexShrink: 0,
              border: '1.5px dashed #b9c9d8', borderRadius: 8, background: 'none',
              color: '#3d5166', fontSize: 11, fontWeight: 700, cursor: 'pointer',
              fontFamily: DS.fontBody }}>
            voir
          </button>
        </div>
      )}

      {expanded && (
        <div style={{ padding: '0 14px 14px', borderTop: `1px solid ${DS.border}` }}>
          {/* Checklist détaillée + photos SOUS CHAQUE PIÈCE. */}
          <ChecklistDetail itemsChecked={run.items_checked} template={template}
            photosByPiece={photosByPiece} onPhotoClick={(idx) => setViewerIdx(idx)} />

          {/* Incident(s) lié(s) — COMPLET inline (brique mutualisée). */}
          {incidents.map((inc) => <IncidentFull key={inc.id} inc={inc} />)}
        </div>
      )}

      {/* Visionneuse plein écran (photos du passage, toutes pièces enchaînées). */}
      {viewerIdx != null && orderedPhotos.length > 0 && (
        <ProprioPhotoViewer photos={orderedPhotos} index={viewerIdx}
          onClose={() => setViewerIdx(null)} onIndex={setViewerIdx} onExpired={onReload} />
      )}
    </div>
  )
}

// ── LA CARTE D'INCIDENT À ÉTATS (lot ⑤, spec §6 + arbitrages 3/4 22/08) ──
// L'état en BANDEAU COLORÉ avec le montant à droite ; puis logement + date,
// l'article, la précision en italique, les photos 44 px, et LA PHRASE qui
// explique sans accuser, sur fond #f8fafc (bloc ciel + icône reçu pour un
// rachat refacturé — « ça touche son argent »).
const ETATS_INCIDENT = {
  rembourse: { bg: '#f0f7f3', bord: '#c9e0d4', fg: '#124a33', icone: Shield, label: 'Remboursé par AirCover' },
  rachete: { bg: '#e8f2fa', bord: '#c3d5e4', fg: '#233240', icone: ShoppingBasket, label: 'Racheté par nos soins' },
  en_cours: { bg: '#fdf7ee', bord: '#e8d4b0', fg: '#8a5f19', icone: Clock, label: 'Demande en cours' },
  a_racheter: { bg: '#fdf4f0', bord: '#e0b5a5', fg: '#8f2f1d', icone: AlertTriangle, label: 'À racheter' },
  regle: { bg: '#f0f7f3', bord: '#c9e0d4', fg: '#124a33', icone: CheckCircle2, label: 'Réglé' },
}

const MOIS_MIN_INC = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet',
  'août', 'septembre', 'octobre', 'novembre', 'décembre']
function dateCourteInc(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || ''))
  return m ? `${Number(m[3])} ${MOIS_MIN_INC[Number(m[2]) - 1]}` : ''
}

export function phraseEtatIncident(d, articles) {
  const art = articles || "l'article"
  if (d.etat === 'rembourse') return 'Nous avons fait la demande, Airbnb a remboursé. Rien à faire de votre côté.'
  if (d.etat === 'rachete') {
    const mois = d.mois ? MOIS_MIN_INC[Number(String(d.mois).slice(5, 7)) - 1] : null
    const montant = d.montant != null ? `${fmtE(d.montant)} apparaîtront sur votre facture${mois ? ` ${/^[aeiouyâ]/.test(mois) ? "d'" : 'de '}${mois}` : ''}.` : null
    return `Nous avons remplacé ${art}.${montant ? ' ' + montant : ''}`
  }
  if (d.etat === 'en_cours') return `Demande AirCover envoyée${d.demandeLe ? ` le ${dateCourteInc(d.demandeLe)}` : ''}. Nous vous tenons au courant.`
  if (d.etat === 'a_racheter') return 'Usure normale, non couverte par AirCover. Nous pouvons le remplacer si vous le souhaitez.'
  return 'Réglé — rien à faire de votre côté.'
}

function IncidentEtatCard({ inc, nomLogement }) {
  const d = etatIncidentProprio(inc)
  const E = ETATS_INCIDENT[d.etat] || ETATS_INCIDENT.regle
  const degs = Array.isArray(inc.degradations) ? inc.degradations : []
  const articles = degs.map((x) => x.type || x.label).filter(Boolean).join(', ') || null
  const photos = (Array.isArray(inc.photos) ? inc.photos : []).map((ph, i) => ({ ...ph, signedUrl: ph.signedUrl || ph.url, idx: i }))
  const [viewerIdx, setViewerIdx] = useState(null)
  const Icone = E.icone
  const dateLabel = dateCourteInc(inc.incident_date || String(inc.reported_at || '').slice(0, 10))
  return (
    <div data-testid={`incident-${inc.id}`}
      style={{ border: '1.5px solid #b9c9d8', borderRadius: 12, background: '#fff',
        overflow: 'hidden', marginBottom: 10 }}>
      {/* L'ÉTAT EN BANDEAU, le montant à droite. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 13px',
        background: E.bg, borderBottom: `1px solid ${E.bord}`, color: E.fg }}>
        <Icone size={15} strokeWidth={2} style={{ flexShrink: 0 }} />
        <span style={{ flex: 1, fontSize: 12, fontWeight: 700 }}>{E.label}</span>
        <span style={{ fontFamily: DS.fontDisplay, fontSize: 15, fontWeight: 700 }}>
          {d.etat === 'en_cours' ? 'en attente' : d.montant != null ? fmtE(d.montant) : ''}
        </span>
      </div>
      <div style={{ padding: '11px 13px' }}>
        <div style={{ fontSize: 12.5, fontWeight: 700, color: '#233240' }}>
          {[nomLogement, dateLabel].filter(Boolean).join(' · ')}
        </div>
        {articles && (
          <div style={{ fontSize: 12, color: '#3d5166', marginTop: 3, fontWeight: 600 }}>{articles}</div>
        )}
        {inc.description && (
          <div style={{ fontSize: 12, color: '#5c6773', fontStyle: 'italic', marginTop: 4, whiteSpace: 'pre-wrap' }}>
            {inc.description}
          </div>
        )}
        {photos.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 9 }}>
            {photos.map((ph) => (
              <img key={ph.idx} src={ph.signedUrl} alt="" onClick={() => setViewerIdx(ph.idx)}
                style={{ width: 44, height: 44, objectFit: 'cover', borderRadius: 7,
                  border: `0.5px solid ${DS.border}`, cursor: 'pointer' }} />
            ))}
          </div>
        )}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, background: d.etat === 'rachete' ? '#e8f2fa' : '#f8fafc',
          border: `1px solid ${d.etat === 'rachete' ? '#c3d5e4' : '#eef2f6'}`, borderRadius: 9,
          padding: '8px 11px', marginTop: 10, fontSize: 11.5, color: '#233240', lineHeight: 1.45 }}>
          {d.etat === 'rachete' && <Receipt size={14} strokeWidth={2} color="#3d5166" style={{ flexShrink: 0, marginTop: 1 }} />}
          <span>{phraseEtatIncident(d, articles)}</span>
        </div>
      </div>
      {viewerIdx != null && photos.length > 0 && (
        <ProprioPhotoViewer photos={photos} index={viewerIdx} onClose={() => setViewerIdx(null)} onIndex={setViewerIdx} />
      )}
    </div>
  )
}

// Mini-barre de synthèse d'un logement : un segment par passage, couleur = statut
// (statusInfo). Liseré corail = passage avec incident. Présentation pure (aucun
// calcul) ; clic sur un segment → ouvre le passage lié (scrollToRun).
function MiniBar({ runs, hasIncident, onSeg }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 9 }}>
      {runs.map((r) => {
        const st = statusInfo(r)
        const inc = hasIncident(r.id)
        const aria = `${headerDateLabel(r)} · ${st.label}${inc ? ' · incident' : ''}`
        return (
          <button key={r.id} type="button" onClick={() => onSeg(r.id)} title={aria} aria-label={aria}
            style={{ flex: '1 1 18px', minWidth: 18, maxWidth: 48, height: 9, borderRadius: 5, border: 'none', padding: 0, cursor: 'pointer', background: st.color, opacity: 0.85, boxShadow: inc ? `0 0 0 1.5px ${DS.corail}` : 'none' }} />
        )
      })}
    </div>
  )
}

// Synthèse de réassurance « N sur M sans souci » + « N à venir » (présentation
// PURE, dérivée des données déjà chargées — aucun montant, aucune écriture).
// On distingue les ménages RÉALISÉS de ceux À VENIR via getEffectiveRunStatus :
//   - FAIT (réalisé) : 'completed' (terminé récent) OU 'ferme_fait' (terminé
//     puis clôturé au-delà de 48h — en prod la quasi-totalité des passages
//     terminés sont >48h, donc 'completed' seul viderait le compteur).
//   - À VENIR (pas encore réalisé) : 'upcoming' (prévu futur), 'scheduled' /
//     'in_progress' (à faire / en cours aujourd'hui).
//   - Les statuts ambigus ('past' non renseigné, 'ferme_non_saisi' jamais
//     saisi, 'skipped', 'anomalie') ne tombent dans AUCUN bucket : on n'affirme
//     jamais « sans souci » sur un passage dont on ignore s'il a eu lieu.
// `noIncidentDoneCount` ne se calcule que sur les passages FAITS (un passage à
// venir ne peut pas être « sans souci » — règle FBZ « textes d'aide = état réel »).
// ── LOT ⑤ (spec §6 + arbitrages 3/4 du 22/08) — dérivations pures. ──

// LA LIGNE VERTE des Ménages : « 12 ménages en août · tous validés · 1 avec
// incident ». `moisLabel` = « en août » ou « sur tout l'historique ».
export function ligneVerteMenages(runs, incidentsByRun, moisLabel) {
  const list = Array.isArray(runs) ? runs : []
  const n = list.length
  const valides = list.filter((r) => r.status === 'completed').length
  const avecIncident = list.filter((r) => (incidentsByRun?.[r.id] || []).length > 0).length
  const morceaux = [`${n} ménage${n > 1 ? 's' : ''} ${moisLabel}`]
  morceaux.push(valides === n && n > 0 ? 'tous validés' : `${valides} validé${valides > 1 ? 's' : ''}`)
  if (avecIncident > 0) morceaux.push(`${avecIncident} avec incident`)
  return morceaux.join(' · ')
}

// L'ÉTAT PROPRIO d'un incident (bandeau coloré, spec §6 + arbitrage 4) —
// dérivé de ses dégradations (etatFinancier + aircoverMontant + statutMenage +
// aircoverDemandeLe, champ additif à venir côté admin). Pire état d'abord :
//   a_racheter > en_cours > rachete > rembourse > regle.
export function etatIncidentProprio(inc) {
  const degs = Array.isArray(inc?.degradations) ? inc.degradations : []
  const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0)
  let aRacheter = 0, aRacheterMontant = 0
  let enCours = 0, enCoursDate = null
  let rachete = 0, racheteMontant = 0, racheteMois = null
  let rembourse = 0, rembourseMontant = 0
  for (const d of degs) {
    const ef = d?.etatFinancier || null
    if (ef === 'Refacturé propriétaire' || ef === 'AirCover + refacturé') {
      rachete += 1
      racheteMontant += num(d.cout)
      if (d.remplaceLe && !racheteMois) racheteMois = String(d.remplaceLe).slice(0, 7)
    }
    if (ef === 'Remboursé AirCover' || ef === 'AirCover + refacturé' || num(d.aircoverMontant) > 0) {
      rembourse += 1
      rembourseMontant += num(d.aircoverMontant)
    }
    if (!ef && d?.aircoverDemandeLe) {
      enCours += 1
      if (!enCoursDate) enCoursDate = d.aircoverDemandeLe
    }
    if (!ef && !d?.aircoverDemandeLe && d?.statutMenage === 'Non récupérable') {
      aRacheter += 1
      aRacheterMontant += num(d.cout)
    }
  }
  if (aRacheter > 0) return { etat: 'a_racheter', montant: aRacheterMontant > 0 ? aRacheterMontant : null }
  if (enCours > 0) return { etat: 'en_cours', montant: null, demandeLe: enCoursDate }
  if (rachete > 0) return { etat: 'rachete', montant: racheteMontant > 0 ? racheteMontant : null, mois: racheteMois }
  if (rembourse > 0) return { etat: 'rembourse', montant: rembourseMontant > 0 ? rembourseMontant : null }
  return { etat: 'regle', montant: null }
}

export function summarizeMenageReassurance(runs, incidentsByRun, todayIso) {
  const incByRun = incidentsByRun || {}
  const doneRuns = []
  const upcomingRuns = []
  for (const r of runs || []) {
    const key = getEffectiveRunStatus(r, todayIso).key
    if (key === 'completed' || key === 'ferme_fait') doneRuns.push(r)
    else if (key === 'upcoming' || key === 'scheduled' || key === 'in_progress') upcomingRuns.push(r)
  }
  const noIncidentDoneCount = doneRuns.filter((r) => !(incByRun[r.id] || []).length).length
  return { doneCount: doneRuns.length, upcomingCount: upcomingRuns.length, noIncidentDoneCount }
}

export default function ProprioMenageView({ partie = 'menages' }) {
  const [state, setState] = useState({ loading: true, error: null, runs: [], reservations: {}, incidents: [], incidentsByRun: {}, logements: [], templatesByLog: {} })
  const [expanded, setExpanded] = useState(() => new Set())
  const [month, setMonth] = useState(() => currentMonthKey())
  const [allMode, setAllMode] = useState(false) // « Tout » = historique complet (exclusif de la nav mois)
  const [logFilter, setLogFilter] = useState('all')
  const [expandedLogs, setExpandedLogs] = useState(() => new Set()) // logements dont « voir les N autres » est ouvert
  const rowRefs = useRef({})
  const [scrollTarget, setScrollTarget] = useState(null)

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: null }))
    try {
      const { from, to } = wideRange()
      const [menageRes, calRes, incRes, logRes] = await Promise.all([
        fetchProprioMenage({}),
        fetchProprioCalendar({ from, to }),
        fetchProprioIncidents({}),
        fetchProprioLogements(),
      ])
      const reservations = {}
      for (const r of calRes.reservations || []) reservations[r.id] = r
      const incidents = incRes.incidents || []
      const incidentsByRun = {}
      for (const inc of incidents) {
        if (!inc.run_id) continue
        if (!incidentsByRun[inc.run_id]) incidentsByRun[inc.run_id] = []
        incidentsByRun[inc.run_id].push(inc)
      }
      setState({
        loading: false, error: null,
        runs: menageRes.runs || [], reservations, incidents, incidentsByRun,
        logements: (logRes.logements || []).map((l) => ({ id: l.id, nom: l.nom })),
        templatesByLog: menageRes.templatesByLog || {},
      })
    } catch (e) {
      setState({ loading: false, error: e.message, runs: [], reservations: {}, incidents: [], incidentsByRun: {}, logements: [], templatesByLog: {} })
    }
  }, [])

  useEffect(() => { load() }, [load])

  const toggle = useCallback((id) => {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id); else next.add(id)
      return next
    })
  }, [])

  const toggleLog = useCallback((logId) => {
    setExpandedLogs((prev) => {
      const next = new Set(prev)
      if (next.has(logId)) next.delete(logId); else next.add(logId)
      return next
    })
  }, [])

  // Mode « Tout » OU mois affiché, + logement → AGISSENT SUR TOUT (passages ET incidents).
  const filteredRuns = useMemo(
    () => state.runs.filter((r) => (logFilter === 'all' || r.logement_id === logFilter) && (allMode || runDateISO(r).slice(0, 7) === month)),
    [state.runs, month, allMode, logFilter],
  )
  const filteredIncidents = useMemo(
    () => state.incidents.filter((inc) => (logFilter === 'all' || inc.logement_id === logFilter) && (allMode || incidentDateISO(inc).slice(0, 7) === month)),
    [state.incidents, month, allMode, logFilter],
  )

  const scrollToRun = useCallback((runId) => {
    if (!runId) return
    const run = state.runs.find((r) => r.id === runId)
    if (run) {
      setMonth(runDateISO(run).slice(0, 7)) // bascule sur le mois du passage lié
      setExpandedLogs((prev) => new Set(prev).add(run.logement_id)) // déplie le logement (anti « voir les N autres »)
    }
    setExpanded((prev) => new Set(prev).add(runId))
    setScrollTarget(runId)
  }, [state.runs])

  useEffect(() => {
    if (!scrollTarget) return
    const el = rowRefs.current[scrollTarget]
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' })
    setScrollTarget(null)
  }, [scrollTarget, filteredRuns])

  const sections = useMemo(() => {
    const byLog = new Map()
    for (const r of filteredRuns) {
      if (!byLog.has(r.logement_id)) byLog.set(r.logement_id, [])
      byLog.get(r.logement_id).push(r)
    }
    const out = []
    for (const [logId, runs] of byLog) {
      const log = state.logements.find((l) => l.id === logId)
      out.push({ logId, logName: log ? log.nom : 'Logement', runs })
    }
    out.sort((a, b) => b.runs.length - a.runs.length)
    return out
  }, [filteredRuns, state.logements])

  function guestOf(run) {
    const resa = run.reservation_id ? state.reservations[run.reservation_id] : null
    if (!resa) return ''
    return `${resa.guest_first_name || ''} ${resa.guest_last_name || ''}`.trim()
  }

  const hasContent = state.runs.length > 0 || state.incidents.length > 0

  return (
    <div>
      {partie === 'menages' ? (
        <SectionTitle icon={Sparkles} sub="Les passages ménage de vos logements (consultation).">Ménages</SectionTitle>
      ) : (
        <SectionTitle icon={AlertTriangle} sub="Les incidents constatés, et où ils en sont (consultation).">Incidents</SectionTitle>
      )}
      <ReadOnlyHint />

      {state.loading && <Loading />}
      {!state.loading && state.error && <ErrorBox message={state.error} onRetry={load} />}

      {!state.loading && !state.error && !hasContent && (
        <EmptyBox>Les passages ménage apparaîtront ici une fois le logement synchronisé.</EmptyBox>
      )}

      {!state.loading && !state.error && hasContent && (
        <>
          {/* Mode « Tout » (exclusif) + navigation par mois — agit sur tout (stats + liste). */}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
            <button onClick={() => setAllMode(true)} className="fbz-press"
              style={{ padding: '7px 14px', borderRadius: DS.radiusControl, border: allMode ? `0.5px solid ${DS.marine}` : `0.5px solid ${DS.borderHover}`, background: allMode ? DS.marine : DS.card, color: allMode ? '#fff' : DS.marine, fontWeight: 700, fontSize: 12, cursor: 'pointer', fontFamily: DS.fontBody }}>Tout</button>
            {/* Nav mois : grisée en mode « Tout », mais cliquer une flèche repasse en mode mois. */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, opacity: allMode ? 0.45 : 1 }}>
              <button onClick={() => { setAllMode(false); setMonth((m) => shiftMonth(m, -1)) }} aria-label="Mois précédent" className="fbz-tap fbz-press"
                style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '5px 9px', borderRadius: DS.radiusControl, border: `0.5px solid ${DS.borderHover}`, background: DS.card, color: DS.marine, lineHeight: 1, cursor: 'pointer', fontFamily: DS.fontBody }}><ChevronLeft size={16} strokeWidth={2} /></button>
              <div style={{ minWidth: 132, textAlign: 'center', fontFamily: DS.fontDisplay, fontWeight: 600, color: DS.marineDark, fontSize: 15 }}>{monthLabel(month)}</div>
              <button onClick={() => { setAllMode(false); setMonth((m) => shiftMonth(m, 1)) }} aria-label="Mois suivant" className="fbz-tap fbz-press"
                style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '5px 9px', borderRadius: DS.radiusControl, border: `0.5px solid ${DS.borderHover}`, background: DS.card, color: DS.marine, lineHeight: 1, cursor: 'pointer', fontFamily: DS.fontBody }}><ChevronRight size={16} strokeWidth={2} /></button>
            </div>
            {(allMode || month !== currentMonthKey()) && (
              <button onClick={() => { setAllMode(false); setMonth(currentMonthKey()) }} className="fbz-press"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '6px 11px', borderRadius: DS.radiusControl, border: `0.5px solid ${DS.borderHover}`, background: DS.card, color: DS.marine, fontWeight: 600, fontSize: 12, cursor: 'pointer', fontFamily: DS.fontBody }}><RotateCcw size={13} strokeWidth={1.8} /> Aujourd&apos;hui</button>
            )}
            {state.logements.length > 1 && (
              <select aria-label="Filtrer par logement" value={logFilter} onChange={(e) => setLogFilter(e.target.value)}
                style={{ marginLeft: 'auto', padding: '6px 9px', borderRadius: DS.radiusControl, border: `0.5px solid ${DS.borderHover}`, background: logFilter !== 'all' ? DS.marine : DS.card, color: logFilter !== 'all' ? '#fff' : DS.marine, fontWeight: 600, fontSize: 12, cursor: 'pointer', fontFamily: DS.fontBody }}>
                <option value="all">Tous les logements</option>
                {state.logements.map((l) => <option key={l.id} value={l.id}>{l.nom}</option>)}
              </select>
            )}
          </div>

          {/* LA LIGNE VERTE (spec §6) — partie Ménages. */}
          {partie === 'menages' && (
            <div data-testid="ligne-verte-menages"
              style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '11px 13px',
                background: '#f0f7f3', border: '1px solid #c9e0d4', borderRadius: 10,
                marginBottom: 12, fontSize: 12.5, fontWeight: 700, color: '#124a33' }}>
              <CheckCircle2 size={16} strokeWidth={2} color="#124a33" style={{ flexShrink: 0 }} />
              {ligneVerteMenages(filteredRuns, state.incidentsByRun, allMode ? "sur tout l'historique" : `en ${monthLabel(month).split(' ')[0].toLowerCase()}`)}
            </div>
          )}

          {/* LES TROIS COMPTEURS (spec §6) — partie Incidents. */}
          {partie === 'incidents' && (() => {
            const etats = filteredIncidents.map((inc) => etatIncidentProprio(inc).etat)
            const nRegles = etats.filter((e) => e === 'regle' || e === 'rembourse' || e === 'rachete').length
            const nEnCours = etats.filter((e) => e === 'en_cours').length
            const nARacheter = etats.filter((e) => e === 'a_racheter').length
            const C = [
              [nRegles, 'réglé', '#f0f7f3', '#c9e0d4', '#124a33'],
              [nEnCours, 'en cours', '#fdf7ee', '#e8d4b0', '#8a5f19'],
              [nARacheter, 'à racheter', '#fdf4f0', '#e0b5a5', '#8f2f1d'],
            ]
            return (
              <div data-testid="compteurs-incidents" style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
                {C.map(([n, lbl, bg, bord, fg]) => (
                  <span key={lbl} style={{ display: 'inline-flex', alignItems: 'baseline', gap: 6,
                    background: bg, border: `1px solid ${bord}`, borderRadius: 10,
                    padding: '8px 13px', color: fg }}>
                    <span style={{ fontFamily: DS.fontDisplay, fontSize: 19, fontWeight: 700 }}>{n}</span>
                    <span style={{ fontSize: 11.5, fontWeight: 700 }}>{lbl}{Number(n) > 1 && lbl !== 'en cours' ? 's' : ''}</span>
                  </span>
                ))}
              </div>
            )
          })()}

          {/* LES INCIDENTS (partie Incidents) — l'état en bandeau coloré. */}
          {partie === 'incidents' && filteredIncidents.length === 0 && (
            <EmptyBox>{allMode ? 'Aucun incident. Tout va bien.' : 'Aucun incident sur ce mois.'}</EmptyBox>
          )}
          {partie === 'incidents' && filteredIncidents.length > 0 && (
            <div style={{ marginBottom: 6 }}>
              {filteredIncidents.map((inc) => (
                <IncidentEtatCard key={inc.id} inc={inc}
                  nomLogement={(state.logements.find((l) => l.id === inc.logement_id) || {}).nom || ''} />
              ))}
            </div>
          )}

          {/* Passages groupés par logement (une carte par bien + mini-barre + repli). */}
          {partie !== 'menages' || state.runs.length === 0 ? null : filteredRuns.length === 0 ? (
            <EmptyBox>{allMode ? 'Aucun passage.' : 'Aucun passage sur ce mois.'}</EmptyBox>
          ) : (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontFamily: DS.fontDisplay, fontSize: 15, fontWeight: 600, color: DS.marineDark, margin: '14px 0 8px' }}>
                <Sparkles size={15} strokeWidth={1.8} color={DS.marine} /> Passages ménage
              </div>
              {sections.map((sec) => {
                const open = expandedLogs.has(sec.logId)
                const shown = open ? sec.runs : sec.runs.slice(0, RUNS_PREVIEW)
                const hidden = sec.runs.length - shown.length
                return (
                  <div key={sec.logId} style={{ ...card, padding: 0, overflow: 'hidden' }}>
                    <div style={{ padding: '12px 14px 11px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontFamily: DS.fontDisplay, fontSize: 14.5, fontWeight: 600, color: DS.marineDark }}>
                          <Home size={15} strokeWidth={1.8} color={DS.marine} /> {sec.logName}
                        </div>
                        <span style={{ fontSize: 11, color: DS.textMuted, fontWeight: 600 }}>{sec.runs.length} passage{sec.runs.length > 1 ? 's' : ''}</span>
                      </div>
                      <MiniBar runs={sec.runs} hasIncident={(id) => (state.incidentsByRun[id] || []).length > 0} onSeg={scrollToRun} />
                    </div>
                    <div>
                      {shown.map((r) => (
                        <MenageRow key={r.id} run={r} guest={guestOf(r)} incidents={state.incidentsByRun[r.id] || []}
                          template={state.templatesByLog[r.logement_id]}
                          expanded={expanded.has(r.id)} onToggle={() => toggle(r.id)} onReload={load}
                          refReg={(el) => { rowRefs.current[r.id] = el }} />
                      ))}
                    </div>
                    {sec.runs.length > RUNS_PREVIEW && (
                      <button type="button" onClick={() => toggleLog(sec.logId)} className="fbz-rowhover"
                        style={{ display: 'block', width: '100%', textAlign: 'center', border: 'none', borderTop: `1px solid ${DS.border}`, background: 'none', cursor: 'pointer', padding: '9px 14px', color: DS.marine, fontWeight: 600, fontSize: 12, fontFamily: DS.fontBody }}>
                        {open ? 'Réduire' : `Voir les ${hidden} autre${hidden > 1 ? 's' : ''} passage${hidden > 1 ? 's' : ''}`}
                      </button>
                    )}
                  </div>
                )
              })}
            </>
          )}
        </>
      )}
    </div>
  )
}
